import axios from 'axios'
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {Navigate, useLocation} from 'react-router-dom'
import {apiUrl} from '../../../../api-utils/constantVariables'
import {AdminAuth, mergeAdminStoredProfile} from './AdminAuth'
import {WithChildren} from '../../../../_metronic/helpers'

export type AdminRoleDetail = {id: number; code: string; name: string} | null

type AdminPermissionsContextValue = {
  loading: boolean
  permissions: Set<string>
  isSuperAdmin: boolean
  roleDetail: AdminRoleDetail
  refresh: () => Promise<void>
}

const AdminPermissionsContext = createContext<AdminPermissionsContextValue>({
  loading: true,
  permissions: new Set(),
  isSuperAdmin: false,
  roleDetail: null,
  refresh: async () => {},
})

export function useAdminPermissions() {
  return useContext(AdminPermissionsContext)
}

/** True if the admin may perform an action gated by `code`. */
export function useCan(code: string): boolean {
  const {permissions, isSuperAdmin, loading} = useAdminPermissions()
  if (loading) return false
  if (isSuperAdmin) return true
  return permissions.has(code)
}

type RequirePermissionProps = {
  code: string
  children: ReactNode
}

/** Route / layout gate: redirects to `/403` when not allowed. */
export const RequirePermission: FC<RequirePermissionProps> = ({code, children}) => {
  const {loading, permissions, isSuperAdmin} = useAdminPermissions()
  const location = useLocation()

  if (!AdminAuth()) {
    return <Navigate to='/auth' replace state={{from: location}} />
  }

  if (loading) {
    return (
      <div className='d-flex flex-center p-10'>
        <span className='spinner-border spinner-border-sm text-primary me-3' />
        <span className='text-gray-700'>Loading permissions…</span>
      </div>
    )
  }

  const allowed = isSuperAdmin || permissions.has(code)
  if (!allowed) {
    return <Navigate to='/403' replace state={{from: location}} />
  }

  return <>{children}</>
}

export const AdminPermissionsProvider: FC<WithChildren> = ({children}) => {
  const [loading, setLoading] = useState(true)
  const [permissions, setPermissions] = useState<Set<string>>(new Set())
  const [isSuperAdmin, setIsSuperAdmin] = useState(false)
  const [roleDetail, setRoleDetail] = useState<AdminRoleDetail>(null)

  const refresh = useCallback(async () => {
    const token = AdminAuth()
    if (!token) {
      setPermissions(new Set())
      setIsSuperAdmin(false)
      setRoleDetail(null)
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const res = await axios.get(`${apiUrl}/admin/auth/me`, {
        headers: {'x-access-token': token},
      })
      const d = res.data?.data
      const perms: string[] = Array.isArray(d?.permissions) ? d.permissions : []
      setPermissions(new Set(perms))
      setIsSuperAdmin(!!d?.is_super_admin)
      const rd = d?.role_detail
      setRoleDetail(
        rd && typeof rd.id === 'number' && rd.code && rd.name
          ? {id: rd.id, code: rd.code, name: rd.name}
          : null
      )
      if (d && typeof d === 'object') {
        mergeAdminStoredProfile({
          role: typeof d.role === 'string' ? d.role : undefined,
          name: typeof d.name === 'string' ? d.name : undefined,
          avatar_url: d.avatar_url ?? undefined,
        })
      }
    } catch {
      setPermissions(new Set())
      setIsSuperAdmin(false)
      setRoleDetail(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  useEffect(() => {
    const onProfile = () => void refresh()
    window.addEventListener('shrido-admin-profile-changed', onProfile)
    return () => window.removeEventListener('shrido-admin-profile-changed', onProfile)
  }, [refresh])

  const value = useMemo(
    () => ({
      loading,
      permissions,
      isSuperAdmin,
      roleDetail,
      refresh,
    }),
    [loading, permissions, isSuperAdmin, roleDetail, refresh]
  )

  return (
    <AdminPermissionsContext.Provider value={value}>{children}</AdminPermissionsContext.Provider>
  )
}
